// Typed wrappers around Office.context.mailbox so the rest of the app
// doesn't have to defend against undefined or worry about the
// distinction between Read and Compose modes.

export type MailItemMode = "read" | "compose" | "none";

export interface MailItemSummary {
  itemId: string | null;          // Graph immutable ID; null in some Compose states (unsaved draft)
  conversationId: string | null;  // for thread-based dossier matching
  subject: string;
  from: string;                   // SMTP address only — display-name stripped
  fromDisplayName: string;
  mode: MailItemMode;
}

/**
 * Returns a snapshot of the currently-displayed item, or null if there is
 * no item context (e.g. add-in opened from outside an item).
 *
 * Read mode → item is Office.MessageRead with sync getters.
 * Compose mode → item is Office.MessageCompose; from/subject use async getters.
 * For sprint 1 we only support Read; Compose support lands when we wire the
 * "Insert reply" flow.
 */
export function getCurrentMailItem(): MailItemSummary | null {
  const item = Office.context.mailbox.item;
  if (!item) return null;

  // Read mode: item is `MessageRead` — sync getters for subject/from/etc.
  if ("itemId" in item && typeof item.itemId === "string") {
    const read = item as Office.MessageRead;
    return {
      itemId: read.itemId,
      conversationId: read.conversationId ?? null,
      subject: read.subject ?? "",
      from: read.from?.emailAddress ?? "",
      fromDisplayName: read.from?.displayName ?? "",
      mode: "read",
    };
  }

  // Compose mode: subject/recipients only via async getters. We surface what
  // is sync-available now (none of the headline fields, sadly) and let the
  // taskpane fetch the rest via getComposeContext() when it actually needs them.
  return {
    itemId: null,
    conversationId: ("conversationId" in item ? (item as { conversationId?: string }).conversationId : null) ?? null,
    subject: "",
    from: "",
    fromDisplayName: "",
    mode: "compose",
  };
}

// ─── Async getters (mode-agnostic where possible) ──────────────────

function asyncGet<T>(fn: (cb: (r: Office.AsyncResult<T>) => void) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    fn((r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
      else reject(new Error(r.error?.message ?? "Office async call failed"));
    });
  });
}

/**
 * Returns the plain-text body of the current item. In Read mode this is
 * the source email; in Compose mode it's whatever's currently in the
 * editor. Truncated to 20k chars (matches backend zod max).
 */
export async function getBodyText(): Promise<string> {
  const item = Office.context.mailbox.item;
  if (!item) return "";
  const text = await asyncGet<string>((cb) => item.body.getAsync(Office.CoercionType.Text, cb));
  return text.length > 20000 ? text.slice(0, 20000) : text;
}

/** Compose-mode: returns subject + first to/from address via async getters. */
export interface ComposeContext {
  subject: string;
  fromEmail: string;
  fromDisplayName: string;
}

export async function getComposeContext(): Promise<ComposeContext> {
  const item = Office.context.mailbox.item;
  if (!item || !("subject" in item)) return { subject: "", fromEmail: "", fromDisplayName: "" };
  const compose = item as Office.MessageCompose;

  const [subject, from] = await Promise.all([
    asyncGet<string>((cb) => compose.subject.getAsync(cb)),
    // `from` is a single recipient — not a list — but Office.js still uses
    // EmailAddressDetails. In some configurations it can throw if not set.
    new Promise<{ emailAddress: string; displayName: string }>((resolve) => {
      try {
        compose.from.getAsync((r) => {
          if (r.status === Office.AsyncResultStatus.Succeeded && r.value) {
            resolve({ emailAddress: r.value.emailAddress, displayName: r.value.displayName });
          } else {
            resolve({ emailAddress: Office.context.mailbox.userProfile.emailAddress ?? "", displayName: Office.context.mailbox.userProfile.displayName ?? "" });
          }
        });
      } catch {
        resolve({ emailAddress: Office.context.mailbox.userProfile.emailAddress ?? "", displayName: Office.context.mailbox.userProfile.displayName ?? "" });
      }
    }),
  ]);

  return { subject, fromEmail: from.emailAddress, fromDisplayName: from.displayName };
}

/**
 * Inserts the drafted reply into Outlook.
 *  - Read mode: opens a new reply form pre-filled with `text`.
 *  - Compose mode: sets the body via setAsync, with conflict detection.
 *
 * For compose-mode conflict detection: if the current body has > 50 non-
 * whitespace chars, we ask the caller to confirm. The caller passes
 * `force: true` after the user accepts the overwrite. Why 50? Anything
 * shorter is probably the empty-quote default Outlook prefills, not real
 * user input.
 */
export interface InsertResult {
  inserted: boolean;
  /** true → caller should confirm with the user and retry with force=true */
  conflict: boolean;
  /** First chars of conflicting content for the prompt */
  conflictPreview?: string;
}

export async function insertReplyBody(text: string, opts: { force?: boolean } = {}): Promise<InsertResult> {
  const item = Office.context.mailbox.item;
  if (!item) return { inserted: false, conflict: false };

  // Read mode → spawn a new reply window
  if (item.itemType === Office.MailboxEnums.ItemType.Message && "displayReplyForm" in item) {
    (item as Office.MessageRead).displayReplyForm({ htmlBody: textToHtml(text) });
    return { inserted: true, conflict: false };
  }

  // Compose mode → setAsync with conflict check
  const compose = item as Office.MessageCompose;
  if (!opts.force) {
    const existing = await asyncGet<string>((cb) => compose.body.getAsync(Office.CoercionType.Text, cb));
    const stripped = existing.replace(/\s+/g, "").length;
    if (stripped > 50) {
      return {
        inserted: false,
        conflict: true,
        conflictPreview: existing.trim().slice(0, 120),
      };
    }
  }

  await new Promise<void>((resolve, reject) => {
    compose.body.setAsync(textToHtml(text), { coercionType: Office.CoercionType.Html }, (r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(new Error(r.error?.message ?? "Kon reply niet invoegen"));
    });
  });

  return { inserted: true, conflict: false };
}

/**
 * Casey returns plain text; Outlook's HTML body wants paragraphs. We
 * escape HTML, then convert blank-line-separated chunks into <p> blocks
 * and single newlines into <br>. No fancy markdown rendering — Casey is
 * instructed to write plain prose.
 */
function textToHtml(text: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0);
  return paragraphs
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/**
 * Office.js SSO token. Returns a JWT signed by Microsoft whose `aud` is
 * `api://outlook.casemeister.com/<azure-app-id>`. The backend validates
 * this via JWKS and exchanges it for a Casemeister session.
 *
 * Errors surface as Office.AsyncResult.error.code — the SSO docs list the
 * codes; common ones are mapped to readable Dutch messages here.
 */
export async function getOfficeSsoToken(): Promise<string> {
  try {
    // Office.auth.getAccessToken (Mailbox 1.5+) is the modern SSO API.
    // The older OfficeRuntime.auth equivalent is functionally identical
    // but not exposed in current @types/office-js.
    return await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: false,
    });
  } catch (err: unknown) {
    const code = (err as { code?: number }).code;
    // Microsoft's SSO error codes (subset we expect):
    // 13001 = user not signed into Office
    // 13002 = user aborted sign-in
    // 13003 = unsupported account type (consumer MSA in some configs)
    // 13005 = consent required and not granted
    // 13006/13007 = client error (transient — caller can retry)
    // 13008 = call already in progress
    // 13010 = running in unsupported environment (Edge legacy etc.)
    if (code === 13001 || code === 13002) {
      throw new Error('Inloggen is geannuleerd. Probeer opnieuw.');
    }
    if (code === 13003) {
      throw new Error('Dit Microsoft-account wordt niet ondersteund. Gebruik je werk-account.');
    }
    if (code === 13005) {
      throw new Error('Toestemming is vereist. Vraag je beheerder om de Casemeister add-in goed te keuren.');
    }
    if (code === 13010) {
      throw new Error('Deze Outlook-versie ondersteunt geen single sign-on.');
    }
    throw new Error('Inloggen mislukt. Probeer het opnieuw.');
  }
}
